import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import './BookingDetail.css';

interface Booking {
  id: string;
  userId: string;
  providerId?: string;
  userInfo: {
    name: string;
    email: string;
    phone?: string;
  };
  subcategoryId: string;
  serviceItem: {
    name: string;
    price: number;
    priceUnit?: string;
    description: string;
    jobDetails: string;
    duration: string;
  };
  originalPrice?: number;
  finalPrice?: number;
  discountCode?: string | null;
  discountApplied?: {
    code: string;
    discountType: 'percentage' | 'fixed_amount';
    discountValue: number;
    discountAmount: number;
  } | null;
  dateTime: string;
  address: string;
  note?: string | null;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'pending_completion';
  paymentMethod?: 'cash' | 'bank_transfer';
  providerQueue?: string[];
  completionReportedAt?: any;
  createdAt: any;
  updatedAt?: any;
  hasReview?: boolean;
}

interface Provider {
  uid: string;
  name: string;
  email: string;
  phoneNumber?: string;
  phone?: string;
  avatar?: string;
  profilePhotoUrl?: string;
}

interface Customer {
  uid: string;
  name: string;
  email: string;
  phoneNumber?: string;
  phone?: string;
  avatar?: string;
  profilePhotoUrl?: string;
}

interface Subcategory {
  id: string;
  name: string;
  description?: string;
  categoryId?: string;
}

interface Review {
  id: string;
  bookingId: string;
  customerId: string;
  providerId: string;
  rating: number;
  comment?: string;
  createdAt: any;
}

interface RatingStarsProps {
  rating: number;
  size?: number;
}

const RatingStars: React.FC<RatingStarsProps> = ({ rating, size = 20 }) => {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          style={{
            color: star <= rating ? '#ffc107' : '#e4e5e9',
            fontSize: size,
            marginRight: '2px'
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
};

interface QueueProvider {
  id: string;
  name: string;
  profilePhotoUrl?: string;
}

const BookingDetail: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [provider, setProvider] = useState<Provider | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [subcategory, setSubcategory] = useState<Subcategory | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [queueProviders, setQueueProviders] = useState<QueueProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
    }
  }, [bookingId]);

  const fetchProviderDetails = async (providerId: string): Promise<QueueProvider | null> => {
    try {
      const providerDoc = await getDoc(doc(db, 'users', providerId));
      if (providerDoc.exists()) {
        const data = providerDoc.data();
        return {
          id: providerDoc.id,
          name: data.name || 'Không xác định',
          profilePhotoUrl: data.profilePhotoUrl
        };
      }
    } catch (error) {
      console.error('Lỗi khi tải thông tin nhà cung cấp trong hàng đợi:', error);
    }
    return null;
  };

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải thông tin booking:', bookingId);

      // Fetch booking information
      const bookingDoc = await getDoc(doc(db, 'bookings', bookingId!));
      if (!bookingDoc.exists()) {
        setError('Không tìm thấy booking');
        return;
      }

      const bookingData = {
        id: bookingDoc.id,
        ...bookingDoc.data()
      } as Booking;
      setBooking(bookingData);

      // Fetch provider information if exists
      if (bookingData.providerId) {
        try {
          const providerDoc = await getDoc(doc(db, 'users', bookingData.providerId));
          if (providerDoc.exists()) {
            const providerData = {
              uid: providerDoc.id,
              ...providerDoc.data()
            } as Provider;
            setProvider(providerData);
          }
        } catch (error) {
          console.error('Lỗi khi tải thông tin nhà cung cấp:', error);
        }
      }

      // Fetch queue providers information
      if (bookingData.providerQueue && bookingData.providerQueue.length > 0) {
        const providersDetails = await Promise.all(
          bookingData.providerQueue.map(providerId => fetchProviderDetails(providerId))
        );
        setQueueProviders(providersDetails.filter((p): p is QueueProvider => p !== null));
      }

      // Fetch customer information
      if (bookingData.userId) {
        try {
          const customerDoc = await getDoc(doc(db, 'users', bookingData.userId));
          if (customerDoc.exists()) {
            const customerData = {
              uid: customerDoc.id,
              ...customerDoc.data()
            } as Customer;
            setCustomer(customerData);
          }
        } catch (error) {
          console.error('Lỗi khi tải thông tin khách hàng:', error);
        }
      }

      // Fetch subcategory information
      if (bookingData.subcategoryId) {
        try {
          const subcategoryDoc = await getDoc(doc(db, 'subcategories', bookingData.subcategoryId));
          if (subcategoryDoc.exists()) {
            const subcategoryData = {
              id: subcategoryDoc.id,
              ...subcategoryDoc.data()
            } as Subcategory;
            setSubcategory(subcategoryData);
          }
        } catch (error) {
          console.error('Lỗi khi tải thông tin subcategory:', error);
        }
      }

      // Fetch review if exists
      try {
        const reviewQuery = query(
          collection(db, 'reviews'),
          where('bookingId', '==', bookingId)
        );
        const reviewSnapshot = await getDocs(reviewQuery);
        if (!reviewSnapshot.empty) {
          const reviewData = {
            id: reviewSnapshot.docs[0].id,
            ...reviewSnapshot.docs[0].data()
          } as Review;
          setReview(reviewData);
        }
      } catch (error) {
        console.error('Lỗi khi tải thông tin đánh giá:', error);
      }

    } catch (error) {
      console.error('Lỗi khi tải thông tin booking:', error);
      setError('Không thể tải thông tin booking');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (newStatus: string) => {
    if (!booking) return;

    try {
      setUpdating(true);
      const bookingRef = doc(db, 'bookings', booking.id);
      await updateDoc(bookingRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      setBooking(prev => prev ? { ...prev, status: newStatus as any } : null);
    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái:', error);
      setError('Cập nhật trạng thái thất bại');
    } finally {
      setUpdating(false);
    }
  };

  const formatDateTime = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('vi-VN');
    } catch (error) {
      return 'N/A';
    }
  };

  const formatPrice = (price: number | undefined) => {
    if (price === undefined || price === null) return 'N/A';
    return price.toLocaleString('vi-VN') + ' VND';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      pending: 'Chờ xử lý',
      confirmed: 'Đã xác nhận',
      cancelled: 'Đã hủy',
      completed: 'Hoàn thành',
      pending_completion: 'Chờ hoàn thành'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getStatusClass = (status: string) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      cancelled: 'status-cancelled',
      completed: 'status-completed',
      pending_completion: 'status-pending-completion'
    };
    return classes[status as keyof typeof classes] || '';
  };

  if (loading) {
    return (
      <div className="booking-detail">
        <div className="page-header">
          <h2>Chi tiết Booking</h2>
        </div>
        <div className="loading">
          <div className="loading-spinner">Đang tải thông tin...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-detail">
        <div className="page-header">
          <h2>Chi tiết Booking</h2>
        </div>
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/bookings')} className="back-button">
          Quay lại
        </button>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="booking-detail">
        <div className="page-header">
          <h2>Chi tiết Booking</h2>
        </div>
        <div className="no-data">Không tìm thấy thông tin booking</div>
        <button onClick={() => navigate('/bookings')} className="back-button">
          Quay lại
        </button>
      </div>
    );
  }

  return (
    <div className="booking-detail">
      <div className="page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Quay lại
        </button>
        <h2>Chi tiết Booking #{booking.id.slice(-8).toUpperCase()}</h2>
        <p className="page-subtitle">Thông tin chi tiết về booking</p>
      </div>

      <div className="detail-content">
        {/* Status and Basic Info */}
        <div className="info-section">
          <h3>Trạng thái & Thông tin cơ bản</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Trạng thái:</label>
              <div className="status-container">
                <span className={`status ${getStatusClass(booking.status)}`}>
                  {getStatusLabel(booking.status)}
                </span>
                <select
                  value={booking.status}
                  onChange={(e) => updateBookingStatus(e.target.value)}
                  disabled={updating}
                  className="status-select"
                >
                  <option value="pending">Chờ xử lý</option>
                  <option value="confirmed">Đã xác nhận</option>
                  <option value="cancelled">Đã hủy</option>
                  <option value="completed">Hoàn thành</option>
                  <option value="pending_completion">Chờ hoàn thành</option>
                </select>
                {updating && <span className="updating">Đang cập nhật...</span>}
              </div>
            </div>
            <div className="info-item">
              <label>Ngày tạo:</label>
              <span>{formatDateTime(booking.createdAt)}</span>
            </div>
            <div className="info-item">
              <label>Cập nhật cuối:</label>
              <span>{formatDateTime(booking.updatedAt)}</span>
            </div>
            <div className="info-item">
              <label>Thời gian dịch vụ:</label>
              <span>{booking.dateTime}</span>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="info-section">
          <h3>Thông tin khách hàng</h3>
          <div className="user-info">
            {customer?.profilePhotoUrl && (
              <img 
                src={customer.profilePhotoUrl} 
                alt="Avatar khách hàng" 
                className="user-avatar"
              />
            )}
            <div className="user-details">
              <div className="info-grid">
                <div className="info-item">
                  <label>Tên:</label>
                  <span>{booking.userInfo.name}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{booking.userInfo.email}</span>
                </div>
                <div className="info-item">
                  <label>Số điện thoại:</label>
                  <span>{booking.userInfo.phone || customer?.phoneNumber || customer?.phone || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Provider Information */}
        <div className="info-section">
          <h3>Thông tin nhà cung cấp</h3>
          {provider ? (
            <div className="user-info">
              {provider.profilePhotoUrl && (
                <img 
                  src={provider.profilePhotoUrl} 
                  alt="Avatar nhà cung cấp" 
                  className="user-avatar"
                />
              )}
              <div className="user-details">
                <div className="info-grid">
                  <div className="info-item">
                    <label>Tên:</label>
                    <span>{provider.name}</span>
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{provider.email}</span>
                  </div>
                  <div className="info-item">
                    <label>Số điện thoại:</label>
                    <span>{provider.phoneNumber || provider.phone || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-provider">Chưa có nhà cung cấp</div>
          )}
        </div>

        {/* Service Information */}
        <div className="info-section">
          <h3>Thông tin dịch vụ</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Tên gói:</label>
              <span>{booking.serviceItem.name}</span>
            </div>
            <div className="info-item">
              <label>Danh mục dịch vụ:</label>
              <span>{subcategory?.name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Mô tả:</label>
              <span>{booking.serviceItem.description}</span>
            </div>
            <div className="info-item full-width">
              <label>Chi tiết công việc:</label>
              <span>{booking.serviceItem.jobDetails}</span>
            </div>
            <div className="info-item">
              <label>Thời gian thực hiện:</label>
              <span>{booking.serviceItem.duration}</span>
            </div>
            <div className="info-item">
              <label>Phương thức thanh toán:</label>
              <span>
                {booking.paymentMethod === 'cash' 
                  ? 'Tiền mặt' 
                  : booking.paymentMethod === 'bank_transfer' 
                    ? 'Chuyển khoản ngân hàng' 
                    : 'Chưa xác định'
                }
              </span>
            </div>
          </div>
        </div>

        {/* Price Information */}
        <div className="info-section">
          <h3>Thông tin giá cả</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Giá gốc:</label>
              <span>{formatPrice(booking.serviceItem.price)}</span>
            </div>
            {booking.originalPrice && booking.originalPrice !== booking.serviceItem.price && (
              <div className="info-item">
                <label>Giá ban đầu:</label>
                <span>{formatPrice(booking.originalPrice)}</span>
              </div>
            )}
            {booking.discountApplied && (
              <>
                <div className="info-item">
                  <label>Mã giảm giá:</label>
                  <span>{booking.discountApplied.code}</span>
                </div>
                <div className="info-item">
                  <label>Giảm giá:</label>
                  <span>
                    {booking.discountApplied.discountType === 'percentage' 
                      ? `${booking.discountApplied.discountValue}%` 
                      : formatPrice(booking.discountApplied.discountValue)
                    } 
                    (-{formatPrice(booking.discountApplied.discountAmount)})
                  </span>
                </div>
              </>
            )}
            <div className="info-item final-price">
              <label>Giá cuối cùng:</label>
              <span className="price-highlight">{formatPrice(booking.finalPrice)}</span>
            </div>
          </div>
        </div>

        {/* Address and Notes */}
        <div className="info-section">
          <h3>Địa chỉ & Ghi chú</h3>
          <div className="info-grid">
            <div className="info-item full-width">
              <label>Địa chỉ thực hiện:</label>
              <span>{booking.address}</span>
            </div>
            {booking.note && (
              <div className="info-item full-width">
                <label>Ghi chú:</label>
                <span>{booking.note}</span>
              </div>
            )}
          </div>
        </div>

        {/* Additional Information */}
        {(booking?.providerQueue && booking.providerQueue.length > 0) && (
          <div className="info-section">
            <h3>Hàng đợi nhà cung cấp</h3>
            <div className="provider-queue">
              {queueProviders.map((queueProvider, index) => (
                <div key={queueProvider.id} className="queue-item">
                  <div className="queue-provider-info">
                    {queueProvider.profilePhotoUrl && (
                      <img 
                        src={queueProvider.profilePhotoUrl} 
                        alt={`Avatar của ${queueProvider.name}`}
                        className="queue-provider-avatar"
                      />
                    )}
                    <div className="queue-provider-details">
                      <span className="queue-number">#{index + 1}</span>
                      <span className="queue-provider-name">{queueProvider.name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {booking.completionReportedAt && (
          <div className="info-section">
            <h3>Báo cáo hoàn thành</h3>
            <div className="info-item">
              <label>Thời gian báo cáo:</label>
              <span>{formatDateTime(booking.completionReportedAt)}</span>
            </div>
          </div>
        )}

        {/* Review Information */}
        {review && (
          <div className="info-section">
            <h3>Đánh giá từ khách hàng</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Đánh giá:</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <RatingStars rating={review.rating} />
                  <span>({review.rating}/5)</span>
                </div>
              </div>
              {review.comment && (
                <div className="info-item full-width">
                  <label>Nhận xét:</label>
                  <span>{review.comment}</span>
                </div>
              )}
              <div className="info-item">
                <label>Thời gian đánh giá:</label>
                <span>{formatDateTime(review.createdAt)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetail; 